(ns ui.enrich.event
  "AI enrichment jobs: request a job, follow every job of the account with a
   standing completion listener (the user may navigate anywhere while they
   run), fetch a job's result when it finishes or when the user reviews it, and
   resolve it once its result is saved or discarded. The top bar's process list
   loads the account's unresolved jobs, so they outlive a reload.

   Handlers are named fns so they can be unit-tested directly, the way
   ui.completion.event's are."
  (:require
   [common.completion :as completion-contract]
   [common.localization :as loc]
   [fx.ajax :as ajax]
   [re-frame.core :as rf]
   [ui.components.toast.view :as toast]
   [ui.completion.event :as completion]
   [ui.enrich.model :as model]))

(defn new-token
  "A token for a screen to follow the job it starts."
  []
  (str (random-uuid)))

(def ^:private listen-fx
  [:dispatch [::completion/listen
              {:listener-id   model/listener-id
               :kind          completion-contract/ai-job-enrichment-kind
               :on-completion [::completion-arrived]}]])

(defn request
  "Start an enrichment job, followed by `token` until the job id comes back.
   Context is either a TP template (`:template-kw`) or a config-driven flow
   (`:entity-type` + `:targets`, one {:entity-type :fields} per entity in the
   flow). `:draft` is the partial data (`:entity-id` for an existing entity);
   `:attachment-ids` the documents to read."
  [{:keys [db]} [_ {:keys [token template-kw entity-type targets draft rule-context attachment-ids]}]]
  {:db (model/add-request db token {:entity-type entity-type
                                    :template-kw template-kw
                                    :draft       draft})
   :fx [listen-fx]
   ::ajax/send {:operation :ai-enrichment/request
                :body      (cond-> {:attachment-ids (vec attachment-ids)}
                             template-kw   (assoc :template-kw template-kw)
                             entity-type   (assoc :entity-type entity-type)
                             (seq targets) (assoc :targets targets)
                             draft         (assoc :draft draft)
                             rule-context  (assoc :rule-context rule-context))
                :on-ok     [::requested token]
                :on-error  [::request-failed token]}})

(defn requested
  [db [_ token resp]]
  (model/request-accepted db token (:body resp)))

(defn request-failed
  [db [_ token _resp]]
  (model/request-failed db token))

(defn load-jobs
  "Load the account's unresolved jobs and follow them."
  [{:keys [db]} _]
  (let [db (model/list-requested db)]
    {:db         db
     :fx         [listen-fx]
     ::ajax/send {:operation :ai-enrichment/list
                  :body      {}
                  :on-ok     [::jobs-loaded (model/list-gen db)]
                  :on-error  [::jobs-load-failed]}}))

(defn jobs-loaded
  "The list is the backend's word on which jobs are unresolved: a held job it
   lacks was resolved elsewhere (see model/reconcile-jobs)."
  [db [_ gen resp]]
  (model/reconcile-jobs db gen (get-in resp [:body :jobs])))

(defn completion-arrived
  "A job of the account finished, possibly one another tab started. Fetch it;
   the fetch then tells the user."
  [_ [_ payload]]
  (if-let [result-id (model/result-id-from-payload payload)]
    {:fx [[:dispatch [::fetch-result result-id {:finished? true}]]]}
    {}))

(defn fetch-result
  "Fetch the job's result. Each fetch has its own id: when fetches overlap,
   only the latest one's reply counts."
  [{:keys [db]} [_ result-id opts]]
  (let [fetch-id (str (random-uuid))]
    {:db         (model/fetch-started db result-id fetch-id)
     ::ajax/send {:operation :ai-enrichment/result
                  :body      {:result-id result-id}
                  :on-ok     [::result-fetched (assoc opts :fetch-id fetch-id)]
                  :on-error  [::result-fetch-failed result-id fetch-id]}}))

(defn result-fetched
  "Store the fetched job. A job that just finished opens the process list on
   it, unless the user is already reviewing it or it is a survey job, which the
   survey editor applies in place."
  [db [_ {:keys [finished? fetch-id]} resp]]
  (let [{:keys [result-id] :as fetched} (:body resp)]
    (if-not (model/current-fetch? db result-id fetch-id)
      db
      (let [db (model/job-loaded db fetched)]
        (if (and finished?
                 (not= result-id (model/reviewing db))
                 (not= :survey (model/job-kind fetched)))
          (model/open-menu-on db result-id)
          db)))))

(defn result-fetch-failed
  "The job could not be read back. Its result may still be fine, so it keeps
   its status, and the process list, opened on it, offers to fetch it again.
   A failure of a fetch a later one replaced changes nothing."
  [db [_ result-id fetch-id _resp]]
  (if (model/current-fetch? db result-id fetch-id)
    (model/fetch-failed db result-id)
    db))

(defn review
  "Open the review of a finished job, fetching its suggestions first when this
   session has not loaded them."
  [{:keys [db]} [_ result-id]]
  (cond-> {:db (-> db
                   (model/set-reviewing result-id)
                   (model/set-menu-open false))}
    (not (:loaded? (model/job db result-id)))
    (assoc :fx [[:dispatch [::fetch-result result-id {}]]])))

(defn close-review
  "Close the review and keep the job in the process list."
  [db _]
  (model/set-reviewing db nil))

(defn resolve-job
  "The result was saved or discarded: drop the job here and on the backend."
  [{:keys [db]} [_ result-id]]
  {:db (model/remove-job db result-id)
   ::ajax/send {:operation :ai-enrichment/resolve
                :body      {:result-id result-id}
                :on-ok     [::resolved]
                :on-error  [::resolve-failed result-id]}})

(defn resolve-failed
  "The backend did not take the job off the list: say so, and list it again,
   so the user can discard or dismiss it again."
  [{:keys [db]} [_ result-id _resp]]
  {:db              (model/resolve-failed db result-id)
   ::tell-not-resolved true
   :fx              [[:dispatch [::load-jobs]]]})

(defn apply-failed
  [db [_ result-id]]
  (model/apply-failed db result-id))

(defn survey-applied
  "A survey editor put the answers of `result-id` in, unsaved. The job stays
   listed until they are saved or the editor is left without saving."
  [db [_ process-id result-id]]
  (model/survey-applied db process-id result-id))

(defn- resolve-survey-job-fx
  "Resolve `result-id` and the survey's older jobs, which a later visit to the
   editor would otherwise apply over its answers."
  [db process-id result-id]
  (mapv (fn [id] [:dispatch [::resolve id]])
        (cons result-id (model/older-survey-jobs db process-id result-id))))

(defn survey-settled
  "The survey editor of `process-id` was left without saving: the applied job
   is done."
  [{:keys [db]} [_ process-id]]
  (let [[db result-id] (model/survey-settled db process-id)]
    (cond-> {:db db}
      result-id (assoc :fx (resolve-survey-job-fx db process-id result-id)))))

(defn survey-saved
  "A save of the survey editor of `process-id` succeeded. `result-id` is the
   job whose answers were applied when the save started, or nil: that job is
   done. One applied after the save started is not, its answers were not in
   the save."
  [{:keys [db]} [_ process-id result-id]]
  (cond-> {:db (model/survey-saved db process-id result-id)}
    result-id (assoc :fx (resolve-survey-job-fx db process-id result-id))))

(defn dismiss-request
  "Drop a request that failed before a job existed."
  [db [_ token]]
  (model/dismiss-request db token))

(defn set-menu-open
  [db [_ open?]]
  (model/set-menu-open db open?))

(defn open-menu-on-job
  [db [_ token]]
  (model/open-menu-on-job db token))

(rf/reg-fx
 ::tell-not-resolved
 (fn [_]
   (toast/error (loc/tr :enrich-process/resolve-failed))))

(rf/reg-event-fx ::request request)
(rf/reg-event-db ::requested requested)
(rf/reg-event-db ::request-failed request-failed)
(rf/reg-event-fx ::load-jobs load-jobs)
(rf/reg-event-db ::jobs-loaded jobs-loaded)
(rf/reg-event-db ::jobs-load-failed (fn [db _] db))
(rf/reg-event-fx ::completion-arrived completion-arrived)
(rf/reg-event-fx ::fetch-result fetch-result)
(rf/reg-event-db ::result-fetched result-fetched)
(rf/reg-event-db ::result-fetch-failed result-fetch-failed)
(rf/reg-event-fx ::review review)
(rf/reg-event-db ::close-review close-review)
(rf/reg-event-fx ::resolve resolve-job)
(rf/reg-event-db ::resolved (fn [db _] db))
(rf/reg-event-fx ::resolve-failed resolve-failed)
(rf/reg-event-db ::apply-failed apply-failed)
(rf/reg-event-db ::survey-applied survey-applied)
(rf/reg-event-fx ::survey-settled survey-settled)
(rf/reg-event-fx ::survey-saved survey-saved)
(rf/reg-event-db ::dismiss-request dismiss-request)
(rf/reg-event-db ::set-menu-open set-menu-open)
(rf/reg-event-db ::open-menu-on-job open-menu-on-job)
